<template lang="pug">
.mt-4
  v-layout(align-center, justify-space-between)
    h2(v-if='isNew') Nieuwe gebruiker
    h2(v-else) Gebruiker wijzigen

  v-card.mt-4.pa-4.ma-1
    v-layout.flex-column-phablet
      text-field.mr-4(
        label='Voorletters',
        v-model='initials',
        mask='A.A.A.A.A.',
        required,
        name='initials',
        :error-messages='initialsValidationErrors',
        @change='$v.initials.$touch()',
        @blur='$v.initials.$touch()'
        )

      text-field(
        label='Achternaam',
        v-model='lastName',
        required,
        name='lastname',
        :error-messages='lastNameValidationErrors',
        @change='$v.lastName.$touch()',
        @blur='$v.lastName.$touch()'
        )

    v-layout.flex-column-phablet
      text-field(
        label='E-mailadres',
        v-model='email',
        :disabled='!isNew',
        required,
        name='email',
        :error-messages='emailValidationErrors',
        @change='$v.email.$touch()',
        @blur='$v.email.$touch()',
        @keyup.enter='save'
        )

      text-field.mr-0(
        label='Telefoon',
        v-model='phone',
        mask='+## # ## ### ###',
        name='phone',
        :error-messages='phoneValidationErrors',
        @change='$v.phone.$touch()',
        @blur='$v.phone.$touch()'
        )

    v-divider.mt-2.mb-4
    b SMS notificaties
    v-layout.flex-column-phablet
      v-checkbox(color='primary', hide-details, label='Nieuwe Financial Lease aanvraag', v-model='notificationTypes.sms.new_business_lease')
      v-checkbox(color='primary', hide-details, label='Nieuwe Kredietaanvraag', v-model='notificationTypes.sms.new_private_lease')
      v-checkbox(color='primary', hide-details, label='Foutmeldingen', v-model='notificationTypes.sms.error')

    v-divider.mt-4.mb-4
    b Email notificaties
    v-layout.flex-column-phablet
      v-checkbox(color='primary', hide-details, label='Nieuwe Financial Lease aanvraag', v-model='notificationTypes.email.new_business_lease')
      v-checkbox(color='primary', hide-details, label='Nieuwe Kredietaanvraag', v-model='notificationTypes.email.new_private_lease')
      v-checkbox(color='primary', hide-details, label='Foutmeldingen', v-model='notificationTypes.email.error')

    template(v-if='isNew')
      v-divider.mt-4.mb-4

      span
        v-icon.mr-2(color='primary') fa fa-info-circle
        | De nieuwe gebruiker ontvangt een activatie email om het wachtwoord in te stellen.
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { fullPhoneRegex } from 'dealerfinanceservices-shared/src/helpers/regex'

export default {
  data () {
    return {
      loading: false,

      initials: null,
      lastName: null,
      email: null,
      phone: null,
      notificationTypes: {
        sms: {
          new_business_lease: false,
          new_private_lease: false,
          error: false
        },

        email: {
          new_business_lease: false,
          new_private_lease: false,
          error: false
        }
      }
    }
  },

  computed: {
    isNew () {
      return this.$route.params.id === 'new'
    },

    anySmsEnabled () {
      return this.notificationTypes.sms.new_business_lease || this.notificationTypes.sms.new_private_lease || this.notificationTypes.sms.error
    },

    initialsValidationErrors () {
      const errors = []
      if (!this.$v.initials.$dirty) return errors
      !this.$v.initials.required && errors.push('Dit veld is verplicht')
      return errors
    },

    lastNameValidationErrors () {
      const errors = []
      if (!this.$v.lastName.$dirty) return errors
      !this.$v.lastName.required && errors.push('Dit veld is verplicht')
      return errors
    },

    emailValidationErrors () {
      const errors = []
      if (!this.$v.email.$dirty) return errors
      !this.$v.email.required && errors.push('Dit veld is verplicht')
      !this.$v.email.email && errors.push('Dit is geen geldig email adres')
      return errors
    },

    phoneValidationErrors () {
      const errors = []
      if (!this.$v.phone.$dirty) return errors
      !this.$v.phone.smsEnabled && errors.push('Een telefoonnummer is nodig om een SMS te kunnen versturen')
      !this.$v.phone.regex && errors.push('Gebruik het volgende formaat: +31 6 12 123 123')
      return errors
    }
  },

  methods: {
    async save () {
      if (this.$v.$invalid) return

      this.loading = true

      const notificationTypes = {
        new_business_lease: [],
        new_private_lease: [],
        error: []
      }

      if (this.notificationTypes.sms.new_business_lease) notificationTypes.new_business_lease.push('sms')
      if (this.notificationTypes.sms.new_private_lease) notificationTypes.new_private_lease.push('sms')
      if (this.notificationTypes.sms.error) notificationTypes.error.push('sms')

      if (this.notificationTypes.email.new_business_lease) notificationTypes.new_business_lease.push('email')
      if (this.notificationTypes.email.new_private_lease) notificationTypes.new_private_lease.push('email')
      if (this.notificationTypes.email.error) notificationTypes.error.push('email')

      if (this.isNew) {
        try {
          await this.$store.dispatch('graphqlMutation', {
            name: 'createUser',
            variables: {
              orgId: this.$store.state.organization.currentOrgId,
              initials: this.initials,
              lastName: this.lastName,
              email: this.email,
              phone: this.phone,
              notificationTypes
            },
            deleteCacheQueries: ['getUsers']
          })

          this.$root.$snackbar.show('Gebruiker aangemaakt')

          this.$router.push({
            name: 'settings users list'
          })
        } catch (error) {
          if (error.message === 'email already in use') {
            window.alert('Dit email adres bestaat al')
          }

          this.$root.$snackbar.show('Gebruiker is NIET aangemaakt')
        }
      } else {
        try {
          await this.$store.dispatch('graphqlMutation', {
            name: 'updateUser',
            variables: {
              orgId: this.$store.state.organization.currentOrgId,
              userId: this.$route.params.id,
              initials: this.initials,
              lastName: this.lastName,
              email: this.email,
              phone: this.phone,
              notificationTypes
            },
            deleteCacheQueries: ['getUsers']
          })

          this.$root.$snackbar.show('Gebruiker gewijzigd')

          this.$router.push({
            name: 'settings users list'
          })
        } catch (error) {
          if (error.message === 'email already in use') {
            window.alert('Dit email adres bestaat al')
          }

          this.$root.$snackbar.show('Gebruiker is NIET gewijzigd')
        }
      }

      this.loading = false
    }
  },

  validations: {
    initials: {
      required
    },

    lastName: {
      required
    },

    email: {
      required,
      email
    },

    phone: {
      smsEnabled: (v, vm) => {
        if (vm.anySmsEnabled) return !!v

        return true
      },
      regex: (v, vm) => {
        if (!v) return true

        return fullPhoneRegex.test(v)
      }
    }
  },

  async created () {
    if (!this.isNew) {
      this.loading = true

      const users = await this.$store.dispatch('graphqlQuery', {
        name: 'getUsers',
        variables: {
          orgId: this.$store.state.organization.currentOrgId
        }
      })

      const user = users.find(x => x.id === this.$route.params.id)

      if (user) {
        this.email = user.email
        this.initials = user.userInfo.initials
        this.lastName = user.userInfo.lastName
        this.phone = user.userInfo.phone
        this.notificationTypes = {
          sms: {
            new_business_lease: user.notificationTypes.new_business_lease.includes('sms'),
            new_private_lease: user.notificationTypes.new_private_lease.includes('sms'),
            error: user.notificationTypes.error.includes('sms')
          },

          email: {
            new_business_lease: user.notificationTypes.new_business_lease.includes('email'),
            new_private_lease: user.notificationTypes.new_private_lease.includes('email'),
            error: user.notificationTypes.error.includes('email')
          }
        }
      } else {
        this.$router.push({
          name: 'settings users list'
        })
      }

      this.loading = false
    }

    for (const key of Object.keys(this.$v.$params).concat('notificationTypes')) {
      this.$watch(key, () => {
        this.$root.$save.show(this.save, null)

        this.$root.$save.setValid(!this.$v.$invalid)
      }, { deep: true })
    }
  },

  watch: {
    'notificationTypes.sms': {
      handler: function () {
        this.$v.phone.$touch()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
